import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Home, Compass } from "lucide-react";

const TYPES = ["House","Office","Factory","Shop","Apartment"];
const DIRS = ["North","South","East","West","North-East","North-West","South-East","South-West"];

export default function Vastu() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", property_type: "House", facing_direction: "North", google_map_link: "", room_analysis: "", recommendations: "" });

  const load = async () => { const { data } = await api.get("/vastu"); setItems(data); };
  useEffect(() => { load(); api.get("/clients").then(r => setClients(r.data)); }, []);

  const create = async () => {
    if (!form.client_id) return toast.error("Select client");
    await api.post("/vastu", form);
    toast.success("Vastu record created"); setOpen(false); load();
  };

  return (
    <div className="space-y-6" data-testid="vastu-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400">Sacred Architecture</div>
          <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight mt-1">Vastu Consultations</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#1E3A8A] btn-pill h-11 px-5" data-testid="new-vastu-btn"><Plus className="w-4 h-4 mr-1.5" /> New assessment</Button></DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle className="font-display text-2xl">New Vastu assessment</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest text-slate-500">Client</Label>
                <Select value={form.client_id} onValueChange={v=>setForm({...form,client_id:v})}>
                  <SelectTrigger><SelectValue placeholder="Choose client" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Field label="Property type">
                <Select value={form.property_type} onValueChange={v=>setForm({...form,property_type:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Facing direction">
                <Select value={form.facing_direction} onValueChange={v=>setForm({...form,facing_direction:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{DIRS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <div className="col-span-2"><Field label="Google map link"><Input value={form.google_map_link} onChange={e=>setForm({...form,google_map_link:e.target.value})} placeholder="https://maps.google.com/..." /></Field></div>
              <div className="col-span-2"><Field label="Room analysis"><Textarea rows={3} value={form.room_analysis} onChange={e=>setForm({...form,room_analysis:e.target.value})} /></Field></div>
              <div className="col-span-2"><Field label="Recommendations"><Textarea rows={3} value={form.recommendations} onChange={e=>setForm({...form,recommendations:e.target.value})} /></Field></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button onClick={create} className="bg-[#1E3A8A] btn-pill">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.length === 0 && <Card className="p-12 rounded-2xl text-center border-dashed col-span-2"><Home className="w-10 h-10 mx-auto text-slate-400" strokeWidth={1.25} /><p className="mt-3 text-slate-500">No Vastu records.</p></Card>}
        {items.map(v => (
          <Card key={v.id} className="p-5 rounded-2xl card-lift" data-testid={`vastu-${v.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <Badge variant="secondary">{v.property_type}</Badge>
                <div className="mt-2 font-display text-lg">Facing {v.facing_direction}</div>
                <div className="text-sm text-slate-500 flex items-center gap-1"><Compass className="w-3 h-3" /> Direction: {v.facing_direction}</div>
              </div>
              <Badge variant={v.status === "completed" ? "default" : "outline"}>{v.status}</Badge>
            </div>
            {v.room_analysis && <div className="mt-3 text-sm text-slate-600 dark:text-slate-300"><span className="text-slate-500 text-xs uppercase tracking-widest">Analysis:</span> {v.room_analysis}</div>}
            {v.recommendations && <div className="mt-2 text-sm p-3 rounded-xl bg-amber-50 dark:bg-amber-500/10"><span className="text-amber-700 dark:text-amber-400 text-xs uppercase tracking-widest">Recommendations:</span><div>{v.recommendations}</div></div>}
            {v.google_map_link && <a href={v.google_map_link} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-blue-600 hover:underline">View on map →</a>}
          </Card>
        ))}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (<div><Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>);
